import { createJSONStorage } from 'jotai/utils';
import { type z } from 'zod';

interface SyncStorage<Value> {
  getItem: (key: string, initialValue: Value) => Value;
  setItem: (key: string, newValue: Value) => void;
  removeItem: (key: string) => void;
  subscribe?: (key: string, callback: (value: Value) => void, initialValue: Value) => (() => void) | undefined;
}

// INVARIANT: This utility MUST be used for all persistent Jotai atoms
// to ensure runtime data integrity and prevent QuotaExceeded crashes.
export function createSafeStorage<T>(
  schema: z.ZodType<T>,
  fallbackValue: T,
  storageType: 'localStorage' | 'sessionStorage' = 'localStorage'
) {
  const baseStorage = createJSONStorage<T>(() => {
    const impl = typeof window !== 'undefined' ? window[storageType] : undefined;
    return (
      impl || {
        getItem: () => null,
        setItem: () => {},
        removeItem: () => {}
      }
    );
  });

  const safeStorage: SyncStorage<T> = {
    getItem: (key: string, initialValue: T): T => {
      try {
        const item = baseStorage.getItem(key, initialValue);
        if (item === null) return fallbackValue;

        // Validate with Zod
        const result = schema.safeParse(item);

        if (!result.success) {
          console.warn(`[SafeStorage] Data validation failed for key "${key}". Resetting to default.`, result.error);
          return fallbackValue;
        }

        return result.data;
      } catch (error) {
        console.error(`[SafeStorage] Failed to load key "${key}".`, error);
        return fallbackValue;
      }
    },

    setItem: (key: string, newValue: T) => {
      try {
        baseStorage.setItem(key, newValue);
      } catch (error) {
        console.error(`[SafeStorage] Failed to save key "${key}".`, error);
      }
    },

    removeItem: (key: string) => {
      baseStorage.removeItem(key);
    },

    subscribe: (key: string, callback: (value: T) => void, initialValue: T) => {
      if (!baseStorage.subscribe) return undefined;

      return baseStorage.subscribe(
        key,
        (value: T) => {
          const result = schema.safeParse(value);
          if (result.success) {
            callback(result.data);
          }
        },
        initialValue
      );
    }
  };

  return safeStorage;
}
